import { TableCell, Tooltip } from '@mui/material';
import type { TableCellProps } from '@mui/material';
import React from 'react';

/**
 * A tooltip that appears only on text the column actually cut off.
 *
 * The tables run `table-layout: fixed` with every body cell set to
 * `white-space: nowrap; overflow: hidden; text-overflow: ellipsis`, which is what keeps a list
 * in-bounds instead of growing a horizontal scrollbar. The cost is that a long value -- job
 * titles most of all, since they routinely differ only at the end ("Senior CAD Designer -
 * Bridal" vs "Senior CAD Designer - Diamonds") -- is readable only up to the column width, with
 * no way to see the rest short of opening the record.
 *
 * **Measured, never assumed.** A tooltip on every cell would be worse than none: hovering a
 * table to find a row would fire a popup over each cell it crossed, most of them repeating text
 * already fully visible. So the title is computed at hover time from the DOM, and an unclipped
 * cell resolves to `''` -- which MUI renders as no tooltip at all.
 */

/** Subpixel rounding puts `scrollWidth` a hair over `clientWidth` on text that fits. */
const OVERFLOW_SLACK = 1;

const overflows = (el: HTMLElement): boolean =>
  el.scrollWidth > el.clientWidth + OVERFLOW_SLACK ||
  el.scrollHeight > el.clientHeight + OVERFLOW_SLACK;

/**
 * True when the cell clips its own text, or when any descendant does.
 *
 * The descendant walk is load-bearing rather than defensive. Several columns render a component
 * that truncates inside its own box -- `RecordLink` and the workspace's status pills both set
 * `text-overflow: ellipsis` on themselves -- and in that case the cell is not overflowing at all,
 * its child is. Measuring only the cell would report those columns as fine while they are the
 * ones being cut.
 */
const isClipped = (el: HTMLElement): boolean => {
  if (overflows(el)) return true;
  return Array.from(el.querySelectorAll<HTMLElement>('*')).some(overflows);
};

/**
 * Ref plus hover handlers for any element that ellipsises. Returns the full text when the
 * element is clipped and `''` when it is not.
 *
 * Exported for `MetricTable`, which renders bare `<td>`s rather than MUI cells.
 */
export function useOverflowTitle<T extends HTMLElement>() {
  const ref = React.useRef<T>(null);
  const [title, setTitle] = React.useState('');

  const measure = React.useCallback(() => {
    const el = ref.current;
    setTitle(el && isClipped(el) ? (el.textContent ?? '').trim() : '');
  }, []);

  return { ref, title, onMouseEnter: measure, onFocus: measure };
}

/**
 * Long enough that dragging the pointer across a table on the way somewhere else does not
 * trail tooltips behind it, short enough to feel like an answer to a deliberate hover.
 *
 * It also makes the measurement order deterministic: `onMouseEnter` sets the title through
 * state, and the delay guarantees that render has happened before MUI decides whether it has a
 * title to show.
 */
export const OVERFLOW_TOOLTIP_ENTER_DELAY_MS = 500;

/**
 * A `TableCell` that explains itself when the column is too narrow for its contents.
 * A drop-in replacement -- every `TableCell` prop passes through.
 */
export const TruncatingTableCell: React.FC<TableCellProps> = ({ children, ...rest }) => {
  const { ref, title, onMouseEnter, onFocus } = useOverflowTitle<HTMLTableCellElement>();

  return (
    <Tooltip disableInteractive enterDelay={OVERFLOW_TOOLTIP_ENTER_DELAY_MS} title={title}>
      <TableCell {...rest} ref={ref} onFocus={onFocus} onMouseEnter={onMouseEnter}>
        {children}
      </TableCell>
    </Tooltip>
  );
};

export default TruncatingTableCell;
